// Central brand + content data for HearthOak Construction

export const BRAND = {
  name: 'HearthOak Construction, LLC',
  short: 'HearthOak',
  tagline: 'Making houses homes',
  phoneDisplay: '817-384-FIXO',
  phoneNumeric: '817-384-3496',
  phoneHref: 'tel:+18173843496',
  email: 'admin@hearthoakhomes.com',
  emailHref: 'mailto:admin@hearthoakhomes.com',
  location: 'Burleson, TX · DFW',
  logoCircle: '/brand/hearthoak-logo-circle.png',
  logoWordmark: '/brand/hearthoak-wordmark.png',
  coverPhoto: 'https://customer-assets.emergentagent.com/job_hearth-build-preview/artifacts/zrtp3e89_Hearthoak%20Cover%20Photo.png',
  composite: 'https://customer-assets.emergentagent.com/job_hearth-build-preview/artifacts/ii25uji0_Hearthoak%20Business%20Cards%20back.png',
  bbbSeal: 'https://seal-austin.bbb.org/seals/blue-seal-81-171-bbb-1000261849.png',
  bbbUrl: 'https://www.bbb.org/us/tx/burleson/profile/general-contractor/hearthoak-construction-llc-0825-1000261849/#sealclick',
  // Real project footage (patio refresh walkthrough), web-optimized pillarbox; mobile gets a smaller cut
  heroVideoSources: [
    { src: '/videos/hero-walkthrough-mobile.mp4', type: 'video/mp4', media: '(max-width: 640px)' },
    { src: '/videos/hero-walkthrough.mp4', type: 'video/mp4' },
  ],
  heroVideoPoster: '/videos/hero-poster.jpg',
};

export const NAV = [
  { label: 'Home', to: '/' },
  { label: 'Services', to: '/services' },
  { label: 'Completed Projects', to: '/gallery' },
  { label: 'About', to: '/about' },
  { label: 'Service Areas', to: '/service-areas' },
  { label: 'Blog', to: '/blog' },
];

export const SERVICES = [
  {
    slug: 'door-installation-repair',
    image: '/portfolio/wood-double-doors.webp',
    title: 'Door Installation & Repair',
    short: 'Exterior, interior, and sliding doors installed and repaired with careful fit, weather sealing, and clean trim work.',
    icon: 'DoorOpen',
    intro:
      'A door is one of the most-used, most-abused parts of a home. Sloppy installation shows up fast — sticking, drafts, gaps, and doors that never quite close right. We install and repair doors with the fit, weather protection, and finish work homeowners expect.',
    bullets: [
      'Exterior door replacement',
      'Interior door installation',
      'Sliding door replacement',
      'Door trim and casing',
      'Brick mold and exterior trim',
      'Weather sealing',
      'Caulking and foam insulation',
      'Paint-ready or finished trim',
    ],
    focus: 'We focus on fit, function, weather protection, security, appearance, and clean finish work.',
    seo:
      'Door installation and repair in Fort Worth, Burleson, Weatherford, Granbury and Cleburne — exterior, interior, and sliding doors installed with clean trim and weather-tight seals.',
    seoTitle: 'Door Installation & Repair in Fort Worth, TX | HearthOak',
    seoDescription:
      'Exterior door replacement, prehung and interior door installation, door frame and trim repair, and weatherstripping across Fort Worth, Burleson & DFW.',
    approach: [
      'Not every door problem means a new door. A door that sticks, drags, or will not latch is often a frame, hinge, or strike-plate issue — and an honest repair costs far less than a replacement. When the slab is damaged, the frame is rotted, or the door was never installed square to begin with, replacement is the better investment. We tell you which situation you are actually in, and why, before any work is quoted.',
      'Proper installation is mostly invisible work. Texas homes settle on expansive clay soil, so rough openings are rarely square. A prehung door has to be shimmed, leveled, and fastened so it swings true and latches cleanly — then sealed. The threshold, weatherstripping, and brick mold are what keep water and drafts out, and they are exactly what rushed installs skip. We set the door, seal the frame, and finish the trim so the opening looks built-in, not swapped.',
      'Every door project ends paint-ready or finished: casing set with consistent reveals, caulk lines cleaned, and hardware adjusted so the door closes the way it should. If the existing trim profile matters to the look of the house, we match it where the profile is still available and tell you plainly when it is not.',
    ],
    faq: [
      {
        q: 'Should my door be repaired or replaced?',
        a: 'It depends on the condition of the slab, the frame, and the sill. Sticking, drafts, and latch problems are often repairable. Rot in the frame or sill, a warped slab, or a door that was never set square usually justifies replacement. We assess the opening and recommend the option that makes sense — not the more expensive one by default.',
      },
      {
        q: 'Can you match my existing door trim and casing?',
        a: 'In most cases, yes. Common profiles are still widely available, and we source matching material where it exists. For discontinued profiles in older DFW homes, we show you the closest match and discuss options before work begins.',
      },
      {
        q: 'What does a door installation estimate include?',
        a: 'A written scope in plain language: the door itself, frame and sill work, brick mold and trim, weather sealing, and the finish level agreed on — paint-ready or finished. You know what is included before work starts.',
      },
    ],
  },
  {
    slug: 'interior-painting',
    image: '/portfolio/dresser-refinish.webp',
    title: 'Interior Painting',
    short: 'Clean lines, honest prep, and even finish coats. Rooms, ceilings, trim, doors, and touch-ups.',
    icon: 'PaintRoller',
    intro:
      'Interior paint that lasts starts with prep — filling, sanding, caulking, masking, and priming what needs it. Our painting is straightforward and detail-focused, with clean lines and consistent finish coats.',
    bullets: ['Walls', 'Ceilings', 'Trim', 'Doors', 'Closets', 'Touch-ups', 'Prep and patching'],
    focus: 'Prep, clean lines, surface repair, and finish consistency — done in a way that respects the home.',
    seo:
      'Interior painting in Fort Worth, Burleson, Weatherford, Granbury and Cleburne — walls, ceilings, trim, doors, and complete room refreshes with careful prep and clean lines.',
    seoTitle: 'Interior Painting Fort Worth, TX | HearthOak Construction',
    seoDescription:
      'Residential interior painting in Fort Worth & DFW — walls, ceilings, trim and doors with honest prep, clean lines, and finishes that hold up.',
    approach: [
      'A paint job is only as good as the prep underneath it. Before a brush comes out, surfaces get filled, sanded, caulked, and spot-primed where they need it. Nail pops, settling cracks, and minor drywall damage are repaired as part of the preparation — not painted over. That is the difference between a finish that still looks right in two years and one that shows every flaw by the first change of season.',
      'Your home gets protected like it is occupied, because it is. Floors and furniture are covered, fixtures are masked, and the work area is put back in order at the end of each day. We choose primers and finish products to suit the surface and the room — high-traffic halls, humid bathrooms, and sunlit rooms do not call for the same coating — and we tell you what is being used and why.',
      'Workmanship standards are simple: straight cut lines, even sheen, consistent coverage, and clean trim. Two coats is the standard on most work. When we leave, the room is ready to live in — no tape left behind, no touch-up list handed to you.',
    ],
    faq: [
      {
        q: 'Do you handle wall repairs before painting?',
        a: 'Yes. Nail holes, settling cracks, and minor drywall damage are repaired and textured to blend as part of prep. Larger repairs are called out in the estimate so the scope is clear before work starts.',
      },
      {
        q: 'How do you protect floors and furniture?',
        a: 'Floors are covered, furniture is moved or masked, and fixtures are protected before any prep begins. Job sites are cleaned up at the end of each day — you should not have to live around our work.',
      },
      {
        q: 'How many coats of paint are included?',
        a: 'Two coats is standard on most interior work. Some colors and substrates need an additional coat or a specific primer, and that is identified in the estimate — not discovered halfway through the job.',
      },
    ],
  },
  {
    slug: 'exterior-painting',
    image: '/portfolio/black-door-red-trim.webp',
    title: 'Exterior Painting',
    short: 'Exterior repaints with proper surface prep, caulking, and durable coatings built to hold up to Texas weather.',
    icon: 'Home',
    intro:
      "An exterior paint job either protects the home for years — or peels in a season. The difference is prep. We scrape, sand, wash, caulk, prime, and repair before a top coat ever goes on. Where appropriate, we specify quality coatings such as Sherwin-Williams Duration.",
    bullets: ['Siding', 'Trim', 'Doors', 'Fascia', 'Surface prep', 'Caulking', 'Spot repairs before paint'],
    focus: 'Durability, prep, weather protection, and protecting the home for the long haul.',
    seo:
      'Exterior painting in DFW — Fort Worth, Burleson, Weatherford, Granbury and Cleburne. Prep-first exterior repaints using quality coatings like Sherwin-Williams Duration.',
    seoTitle: 'Exterior House Painting Fort Worth & DFW | HearthOak',
    seoDescription:
      'Prep-first exterior painting for Texas homes — siding, trim, fascia and doors across Fort Worth, Burleson, Weatherford, Granbury & Cleburne.',
    approach: [
      'North Texas weather is hard on paint. Summer UV breaks coatings down, wind-driven rain finds every failed caulk joint, and the clay soil under most DFW homes keeps trim and siding moving year to year. An exterior repaint here is not a cosmetic job — it is the weatherproofing layer of the house, and it only performs if the surface underneath is sound.',
      'That is why preparation takes most of the time on our exterior work. We wash, scrape, and sand failing paint; re-caulk joints, seams, and trim transitions; prime bare and repaired areas; and take care of spot repairs — soft fascia, weathered trim, minor wood rot — before top coats go on. Coating selection follows the surface and exposure, with quality products such as Sherwin-Williams Duration specified where they make sense for the home.',
      'The goal is straightforward: a finish that still looks right and still protects the house years later, not one that peels after the first hard summer. We walk the property with you before quoting so the written scope reflects what the house actually needs.',
    ],
    faq: [
      {
        q: 'How do I know when my home needs repainting?',
        a: 'Chalking on your hand when you rub the siding, hairline cracking at caulk joints, fading, peeling, and any bare or exposed wood are the common warning signs. Two or three of those together usually mean it is time — and repainting at that stage is far less expensive than waiting for substrate damage.',
      },
      {
        q: 'What preparation is included in an exterior repaint?',
        a: 'Washing, scraping and sanding failed paint, re-caulking joints and trim transitions, priming bare and repaired areas, and masking what should not get painted. Spot repairs to trim or fascia are identified in the estimate so there are no surprises.',
      },
      {
        q: 'Does the Texas climate change which paint you use?',
        a: 'Yes. South- and west-facing elevations take the worst UV exposure, and product choice has to account for it. We specify coatings suited to the surface and the exposure and explain the reasoning in plain language.',
      },
    ],
  },
  {
    slug: 'drywall-texture-repairs',
    image: '/portfolio/gym-hex-ceiling.webp',
    title: 'Drywall, Texture & Repairs',
    short: 'Patches, tape and bed, texture matching, and paint-ready repairs that blend in — not stand out.',
    icon: 'Wrench',
    intro:
      'Good drywall repair is judged by what you cannot see. The point is to blend the repair into the wall so it disappears under paint. We handle patches large and small, tape-and-bed, texture matching, and finish so the surface is truly paint-ready.',
    bullets: [
      'Drywall patches',
      'Tape and bed',
      'Texture matching (knockdown, orange peel, smooth)',
      'Paint-ready repairs',
      'Water damage patching',
      'Small wall repairs',
    ],
    focus: 'Blending. If the patch shows through the paint, the job is not done.',
    seo:
      'Drywall repair and texture matching in Fort Worth, Burleson, Weatherford, Granbury and Cleburne — patches, tape and bed, water damage, and paint-ready blends.',
    seoTitle: 'Drywall Repair Fort Worth, TX | HearthOak Construction',
    seoDescription:
      'Drywall patching, ceiling repair, water-damaged drywall and orange peel / knockdown texture matching in Fort Worth, Burleson & across DFW.',
    approach: [
      'Most drywall damage in DFW homes comes from a short list: settling cracks as the clay soil moves, doorknob and furniture impacts, removed fixtures, plumbing or roof leaks, and the holes left behind by old repairs done badly. Each one has a correct repair method — patches backed and floated properly, tape-and-bed where joints have failed, and full cut-back to solid material where water has damaged the board.',
      'The part that separates a real repair from a visible one is texture matching. Orange peel, knockdown, and smooth finishes each need their own mud consistency, application, and timing, and the blend has to be feathered into the surrounding wall so no boundary shows. We check work under raking light — the low-angle light that reveals bad patches in the evening — before anything is called finished.',
      'Repairs are left truly paint-ready: primed so the patch does not flash through the finish coats, and blended so the repair disappears. If you have had a leak, we also make sure the moisture source has been addressed before closing the wall back up.',
    ],
    faq: [
      {
        q: 'Will the patch show after the wall is painted?',
        a: 'That is the standard we work to — the repair should disappear under paint. Matching the existing texture, feathering the blend into the surrounding wall, and priming correctly are what make that happen. We check the finish under raking light before calling it done.',
      },
      {
        q: 'Can you match my existing wall texture?',
        a: 'Yes — knockdown, orange peel, smooth, and most common regional textures. Texture matching is a skill job, and it is the part of drywall repair we are most often called in to fix after someone else attempted it.',
      },
      {
        q: 'Can you repair water-damaged drywall?',
        a: 'Yes, once the source of the water has been fixed. Damaged board is cut back to solid material, the area is checked for lingering moisture, and the wall is rebuilt, textured, and left paint-ready.',
      },
    ],
  },
  {
    slug: 'trim-carpentry',
    image: '/portfolio/gym-wide.webp',
    title: 'Trim & Carpentry',
    short: 'Baseboards, casing, closets, shelving, and small framing done with care for fit and finish.',
    icon: 'Ruler',
    intro:
      'Finish carpentry is where craftsmanship shows. Tight miters, consistent reveals, solid backing, and clean caulk lines — the small things that make trim look built-in instead of tacked-on.',
    bullets: [
      'Baseboards',
      'Door casing',
      'Window trim',
      'Closet builds',
      'Shelving',
      'Small framing and finish repairs',
    ],
    focus: 'Fit, finish, durability, and practical solutions tailored to the home.',
    seo:
      'Trim and finish carpentry in DFW — baseboards, casing, closet builds, and shelving for homes in Fort Worth, Burleson, Weatherford, Granbury and Cleburne.',
    seoTitle: 'Trim & Finish Carpentry Fort Worth, TX | HearthOak',
    seoDescription:
      'Baseboard and crown molding installation, door and window casing, closet builds, interior trim and wood-rot repair across Fort Worth & DFW.',
    approach: [
      'Finish carpentry is precision work done against imprecise houses. Walls bow, floors slope, and corners drift out of square as DFW homes settle — so tight miters and consistent reveals come from scribing and fitting trim to the house as it actually is, not as it was drawn. That fitting work is what makes trim look built-in rather than tacked on.',
      'Material choice matters and we talk through it honestly. Paint-grade MDF and finger-jointed pine are the right call for most painted trim. Solid poplar or stain-grade hardwood earns its cost when the trim will be stained. For exterior and moisture-prone areas, we address wood rot properly — cutting back to sound material instead of filling over softness.',
      'Our carpentry work ranges from single-room baseboard and quarter round after a flooring replacement to full closet builds, shelving, and small framing. Recent projects include a complete home-gym room with crown molding and a custom cedar gate — the kind of varied finish work that lives in our project gallery.',
    ],
    faq: [
      {
        q: 'Can you match trim profiles in an older home?',
        a: 'Where the profile is still manufactured, yes. Older Fort Worth-area homes often have discontinued profiles — in that case we show you the closest available match or discuss options for the affected rooms before work begins.',
      },
      {
        q: 'Is caulking and painting the trim included?',
        a: 'Trim is installed, filled, caulked, and left paint-ready as standard. Finish painting or staining can be included in the scope — it is spelled out in the written estimate either way.',
      },
      {
        q: 'Do you take small carpentry jobs?',
        a: 'Yes. A single room of baseboard, a closet build-out, or a short punch list of trim repairs are all normal jobs for us. Small work gets the same written scope as large work.',
      },
    ],
  },
  {
    slug: 'small-construction-repair',
    image: '/portfolio/lattice-patio.webp',
    title: 'Small Construction & Repair',
    short: 'Handyman-style project work: repairs, small builds, and the jobs bigger companies won\'t take on.',
    icon: 'Hammer',
    intro:
      "A lot of what homeowners need falls between a full remodel and a one-hour fix. That's the work we're built for — small construction projects, repair sequences, and the punch-list items that keep piling up.",
    bullets: [
      'Small interior repairs',
      'Water damage follow-up work',
      'Small builds and enclosures',
      'Fence and gate repairs',
      'Accent wall installs',
      'Punch-list and turnover work',
    ],
    focus: 'Owner-led, communicated clearly, and completed instead of half-finished.',
    seo:
      'Small construction and repair projects in DFW — owner-led handyman-style work for homes in Fort Worth, Burleson, Weatherford, Granbury and Cleburne.',
    seoTitle: 'Home Repair Contractor Fort Worth, TX | HearthOak',
    seoDescription:
      'Owner-led handyman and home repair services in Fort Worth & DFW — punch lists, water-damage follow-up, fence and gate repair, and small builds.',
    approach: [
      'Most homes accumulate a list: the gate that sags, the accent wall you want added, the water-damage repairs that were never finished after the leak was fixed, the turnover work a rental needs between tenants. These projects are too small for a remodel contractor and too involved for a one-hour fix — and they are exactly the work HearthOak is built around.',
      'Every project is owner-led. The person who walks the job and writes the estimate is the person responsible for the work, so nothing gets lost between a bid and a crew that has never met you. Scope is written in plain language, progress is communicated as the job moves, and the work is finished — not left at ninety percent while the crew moves on.',
      'Grouping helps. If you have several small repairs, bundling them into one visit usually makes the work more efficient and the estimate simpler. Send photos and a list, and we will tell you honestly what belongs together and what does not need doing at all.',
    ],
    faq: [
      {
        q: 'What counts as a small construction or repair project?',
        a: 'Repairs, small builds, punch lists, and the in-between work bigger companies pass on: fence and gate repair, accent walls, enclosures, water-damage follow-up, fixture and trim repairs, and turnover work for rentals. If you are unsure whether your project fits, ask — we will tell you straight.',
      },
      {
        q: 'Do you work with landlords and property managers?',
        a: 'Yes. Turnover repairs, punch-list completion, and make-ready work for rental properties across DFW are a regular part of what we do, with clear written scope for your records.',
      },
      {
        q: 'Can I bundle several small repairs into one visit?',
        a: 'Absolutely — it is usually the most efficient way to handle a list. Send photos of each item and we will scope them together in a single written estimate.',
      },
    ],
  },
  {
    slug: 'permanent-lighting',
    image: '/portfolio/permanent-lighting-night.webp',
    title: 'Permanent Lighting',
    icon: 'Lightbulb',
    short: 'ShawTech permanent LED lighting for rooflines and outdoor spaces — installed once, enjoyed year-round.',
    intro:
      'HearthOak Construction is an Authorized Installation Partner of ShawTech Lighting. Permanent lighting gives your home professional roofline and accent lighting that stays up year-round — colors, scenes, and schedules controlled from an app. No more climbing ladders and hanging strands every holiday season.',
    bullets: [
      'Permanent roofline LED lighting',
      'App-controlled colors, scenes, and schedules',
      'Holiday, game-day, and everyday accent looks',
      'Installed by an Authorized ShawTech Installation Partner',
      'Clean, low-profile mounting along the roofline',
    ],
    focus:
      'Authorized ShawTech Installation Partner — clean mounting, tidy wiring, and a full walkthrough of the app before we leave.',
    seo: 'Permanent LED roofline and holiday lighting in Fort Worth, Burleson, Weatherford, Granbury and Cleburne — installed by an Authorized ShawTech Lighting Installation Partner.',
    seoTitle: 'Permanent Lighting Installation Fort Worth, TX | HearthOak',
    seoDescription:
      'Permanent LED roofline & holiday lighting across Fort Worth, Burleson & DFW — installed by HearthOak Construction, an Authorized ShawTech Lighting Installation Partner.',
    approach: [
      'Permanent lighting replaces the yearly ritual of hanging and taking down holiday strands. Low-profile LED track is mounted along the roofline once, and from then on the look of the house is controlled from an app — warm white for everyday curb appeal, colors and scenes for holidays and game days, schedules so it runs itself.',
      'The quality of a permanent lighting job is in the installation: track matched and mounted straight along the roofline, wiring routed out of sight, connections made to stand up to Texas weather. As an Authorized Installation Partner of ShawTech Lighting, we install the genuine ShawTech system the way it is designed to be installed.',
      'Every install ends with a walkthrough: we connect the system to your app, show you how to build scenes and schedules, and make sure you are comfortable running it before we call the job done.',
    ],
    faq: [
      {
        q: 'Is permanent lighting just for Christmas?',
        a: 'No — that is the point of it. The system stays up year-round and runs warm white or subtle accent looks most of the year, then switches to full color for holidays, game days, or events, all from the app. Nothing to hang or take down again.',
      },
      {
        q: 'Who makes the lighting system?',
        a: 'The system is made by ShawTech Lighting. HearthOak Construction is an Authorized Installation Partner — we handle the design layout, mounting, wiring, and setup of the genuine ShawTech system on your home.',
      },
      {
        q: 'How do I get a price for my home?',
        a: 'Every roofline is different, so pricing follows a look at your home. Send your address and a photo or two of the front roofline through the estimate form or the chat, and the owner will follow up with a written scope.',
      },
    ],
  },
  {
    slug: 'flooring',
    image: '/portfolio/flooring-lvp-after.webp',
    title: 'Flooring',
    icon: 'Layers',
    short: 'Luxury vinyl plank and laminate installation — including the subfloor repair that makes new flooring last.',
    intro:
      'New flooring is only as good as what is underneath it. We install luxury vinyl plank and laminate flooring the right way — and when a floor needs more than a surface swap, we handle that too: tear-out, subfloor repair and rebuilding, leveling, and clean transitions and trim to finish.',
    bullets: [
      'Luxury vinyl plank (LVP) & laminate installation',
      'Subfloor repair, rebuilding & leveling',
      'Tear-out of damaged or worn flooring',
      'Transitions, thresholds & baseboard refit',
      'Floor vent and trim detailing',
    ],
    focus:
      'Subfloor-first flooring — we fix what is underneath before we cover it, so the new floor stays flat, quiet, and tight.',
    seo: 'Flooring installation in Fort Worth, Burleson, Weatherford, Granbury and Cleburne — LVP and laminate installed over properly repaired, level subfloors.',
    seoTitle: 'Flooring Installation Fort Worth, TX | HearthOak',
    seoDescription:
      'Luxury vinyl plank & laminate flooring installation across Fort Worth, Burleson & DFW — including subfloor repair and leveling done before the new floor goes down.',
    approach: [
      'A surprising number of flooring jobs are really subfloor jobs. Soft spots, squeaks, dips, and water damage do not get fixed by covering them with new plank — they get worse. When we open a floor and find damage, we say so, show you, and scope the repair before anything new goes down.',
      'Preparation is the difference between a floor that lasts and one that telegraphs every flaw: damaged sections cut back to solid material, joists and subfloor rebuilt where needed, surfaces leveled, and underlayment matched to the product. Only then does the new flooring go in — with proper expansion gaps and clean, straight courses.',
      'Finish work matters on floors: transitions that sit flush, thresholds that do not trip, baseboards and quarter round refitted neatly, and vents and trim detailed rather than worked around. The goal is a floor that looks like it was always there.',
    ],
    faq: [
      {
        q: 'My floor has soft spots — can you still install new flooring?',
        a: 'Yes, but the soft spots get fixed first. Soft or dipping areas usually mean subfloor damage, and new flooring over a bad subfloor fails early. We open it up, repair or rebuild the damaged sections, then install the new floor on a solid base.',
      },
      {
        q: 'What flooring types do you install?',
        a: 'Luxury vinyl plank and laminate are the core of our flooring work — durable, good-looking, and well suited to busy homes. If you have a different material in mind, ask and we will tell you honestly whether it is a job for us.',
      },
      {
        q: 'Do you handle the tear-out and disposal?',
        a: 'Yes. Tear-out of the old flooring is part of the scope, along with any subfloor repair we find, and the written estimate spells out what is included before work starts.',
      },
    ],
  },
];

export const CITIES = [
  {
    slug: 'fort-worth',
    name: 'Fort Worth',
    blurb:
      'From older TCU-area homes to newer builds on the north side, Fort Worth homes benefit from careful repair work, honest paint prep, and finish carpentry that lasts.',
    seoTitle: 'Handyman & Home Repair in Fort Worth, TX | HearthOak',
    seoDescription:
      'Owner-led home repair, drywall, painting, door installation and finish carpentry across Fort Worth, TX. Clear written estimates from HearthOak Construction.',
    intro: [
      'Fort Worth covers a lot of ground — 1920s bungalows in Fairmount and near TCU, post-war ranches, and steady new construction pushing north and west. That range shows up in the work: older homes need doors re-fitted to frames that have shifted and trim profiles matched that are no longer sold, while newer builds start needing their first real repaint and drywall touch-ups as they settle.',
      'HearthOak Construction works across Fort Worth with the same approach everywhere: owner-led jobs, written scope in plain language, and prep done properly before finish work begins. Whether it is a single sticking door or a whole list of repairs before a sale, you deal directly with the owner from estimate to completion.',
    ],
    localNotes: [
      'Expansive clay soil across Tarrant County keeps foundations and frames moving — a common cause of sticking doors, drywall cracks, and trim gaps.',
      'Older neighborhoods often have original trim and door profiles worth repairing rather than replacing.',
      'Hail and storm seasons make exterior caulk, trim, and paint condition worth checking regularly.',
    ],
    faq: [
      {
        q: 'Do you serve all Fort Worth neighborhoods?',
        a: 'Yes — from the older central neighborhoods to the newer north and west side. If you are in Fort Worth or just outside it, send the project details and we will confirm scheduling.',
      },
      {
        q: 'What projects are most common in Fort Worth homes?',
        a: 'Drywall cracking from soil movement, doors that no longer latch or seal, exterior paint and caulk failure from sun and storms, and trim work after flooring replacements are the jobs we see most.',
      },
      {
        q: 'How do I get an estimate for work in Fort Worth?',
        a: 'Send photos and a short description through the estimate form or the chat on this site. The owner reviews every request personally and follows up directly.',
      },
    ],
  },
  {
    slug: 'burleson',
    name: 'Burleson',
    blurb:
      "Burleson is home base. We work on doors, trim, drywall, interior and exterior paint, and small construction projects for Burleson homeowners and property managers.",
    seoTitle: 'Home Repair & Handyman Services in Burleson, TX | HearthOak',
    seoDescription:
      'HearthOak Construction is based in Burleson, TX — owner-led home repairs, painting, drywall, doors and carpentry for Burleson homeowners and property managers.',
    intro: [
      'Burleson is home base for HearthOak Construction. The town has grown fast, and much of its housing — built from the 1990s through the 2010s — is reaching the age where original paint, builder-grade trim, and contractor-grade doors all start needing attention at once. That is the everyday work we do here.',
      'Being local matters practically, not just sentimentally. Estimates get scheduled without a long drive in either direction, follow-up visits are easy, and the work is done by an owner who lives in the same community as the homes he works on. We serve Burleson homeowners, landlords, and property managers with the full range of repair, painting, drywall, door, and carpentry services.',
    ],
    localNotes: [
      'Many Burleson homes are hitting the 15–30 year mark — the point where original exterior paint, caulk, and builder-grade trim need real attention.',
      'Local clay soil movement means drywall cracks and sticking doors are routine maintenance items, not signs of disaster.',
      'Rental turnover in a growing market makes punch-list and make-ready work a steady need for property managers.',
    ],
    faq: [
      {
        q: 'Are you actually based in Burleson?',
        a: 'Yes. HearthOak Construction is based in Burleson, which means faster estimate scheduling and easy follow-up on any work we do here.',
      },
      {
        q: 'Do you take small jobs in Burleson, or only larger projects?',
        a: 'Both. A single door repair or a room of paint is as welcome as a whole-house punch list — small jobs are a core part of the work, not a favor.',
      },
      {
        q: 'Do you work with Burleson rental properties?',
        a: 'Yes. Turnover repairs and make-ready punch lists for landlords and property managers are a regular part of our Burleson work.',
      },
    ],
  },
  {
    slug: 'weatherford',
    name: 'Weatherford',
    blurb:
      'Weatherford homes see real Texas weather. Exterior paint prep, door weather-sealing, and durable trim work matter a lot here.',
    seoTitle: 'Home Repair & Contractor in Weatherford, TX | HearthOak',
    seoDescription:
      'Home repair, exterior painting, drywall and carpentry in Weatherford, TX. Owner-led work, written estimates — HearthOak Construction serves Parker County.',
    intro: [
      'Weatherford sits far enough west to catch the weather honestly — open exposure, hard summer sun on south and west elevations, and wind-driven rain that tests every caulk joint and door seal on the house. Homes around the historic square mix with acreage properties and newer developments, and each era brings its own repair list.',
      'HearthOak Construction serves Weatherford with prep-first exterior painting, door weather-sealing and replacement, drywall repair, and finish carpentry. The approach stays the same as everywhere we work: the owner walks the job, the scope is written in plain language, and the preparation gets done before the finish does.',
    ],
    localNotes: [
      'Open exposure west of Fort Worth means exterior paint and caulk take more punishment — prep and coating choice matter more here, not less.',
      'Older homes near the town center often have original doors and trim that reward careful repair over replacement.',
      'Acreage and semi-rural properties commonly need fence, gate, and outbuilding repairs alongside house work.',
    ],
    faq: [
      {
        q: 'Do you serve properties outside Weatherford city limits?',
        a: 'Yes — acreage and rural properties around Weatherford and Parker County are part of our normal service area. Send the project details and we will confirm scheduling.',
      },
      {
        q: 'What exterior work do Weatherford homes need most?',
        a: 'Repaints with proper prep, re-caulking of trim and siding joints, door weather-sealing, and spot repair of weathered fascia and trim are the most common needs we see.',
      },
      {
        q: 'Can you handle a mixed list — house repairs plus a gate or fence?',
        a: 'Yes. Bundling house repairs with fence and gate work into one scoped visit is normal for us and usually the most efficient way to get it all done.',
      },
    ],
  },
  {
    slug: 'granbury',
    name: 'Granbury',
    blurb:
      'Lake-area homes and older properties in Granbury need reliable repair work, drywall touch-ups after seasonal shifts, and clean finish carpentry.',
    seoTitle: 'Handyman & Home Repair Services in Granbury, TX | HearthOak',
    seoDescription:
      'Home repair, drywall, painting and carpentry in Granbury, TX — including lake-area and seasonal homes. Owner-led work by HearthOak Construction.',
    intro: [
      'Granbury mixes historic properties near the square, established neighborhoods, and lake-area homes — including places that sit empty for stretches between visits. Homes that are not lived in full-time develop their own patterns: weather seals age unnoticed, small leaks become drywall damage, and exterior paint fails faster than anyone expects because nobody is there to see the early signs.',
      'HearthOak Construction serves Granbury homeowners with repair, painting, drywall, door, and carpentry work — including the catch-up projects that come with seasonal and lake properties. We communicate clearly, document scope in writing, and keep you informed while the work moves, which matters even more when you are not on site every day.',
    ],
    localNotes: [
      'Lake-area humidity and temperature swings accelerate caulk failure, paint breakdown, and door movement.',
      'Seasonal and part-time homes benefit from grouped repair visits — one trip that handles the whole list.',
      'Older properties around the historic district often need texture-matched drywall repair and profile-matched trim work.',
    ],
    faq: [
      {
        q: 'Do you work on lake houses and seasonal properties around Granbury?',
        a: 'Yes. We regularly handle catch-up repair lists for homes that are not occupied full-time, and we keep owners updated with clear communication while the work is underway.',
      },
      {
        q: 'Can you group a season of small repairs into one visit?',
        a: 'That is the ideal way to handle a seasonal property. Send photos of everything on the list and we will scope it as one written estimate.',
      },
      {
        q: 'What should Granbury homeowners watch for between visits?',
        a: 'Failed caulk around windows and doors, peeling or chalking paint, soft trim, and any drywall staining that could signal a slow leak — catching these early keeps repairs small.',
      },
    ],
  },
  {
    slug: 'cleburne',
    name: 'Cleburne',
    blurb:
      'We help Cleburne homeowners with everything from door installs and interior repaints to drywall repair and small construction projects.',
    seoTitle: 'Home Repair & Carpentry in Cleburne, TX | HearthOak',
    seoDescription:
      'Home repair, carpentry, painting, drywall and door services in Cleburne, TX. Owner-led work with written estimates — HearthOak Construction.',
    intro: [
      'Cleburne has some of the older housing stock in our service area — established neighborhoods with original doors, trim, and details that give the homes their character. That character is worth keeping. Much of our Cleburne work is repair-first: re-fitting an original door instead of replacing it, matching a discontinued trim profile, blending a drywall repair into a wall that has been painted a dozen times.',
      'HearthOak Construction serves Cleburne homeowners with the full range of home repair, carpentry, painting, drywall, and door services. Every job is owner-led with a written scope, and older homes get the extra patience their details deserve.',
    ],
    localNotes: [
      'Older Cleburne homes often have original millwork and doors — repair and matching usually beats replacement, and we say so when it does.',
      'Decades of layered paint and past repairs make proper prep and texture matching especially important.',
      'Johnson County clay soil movement keeps drywall cracks and sticking doors on the routine maintenance list.',
    ],
    faq: [
      {
        q: 'Can you work with the original features in an older Cleburne home?',
        a: 'Yes — preserving original doors, trim, and details where they are sound is often the best outcome, and we recommend repair over replacement whenever it makes sense.',
      },
      {
        q: 'What services do you offer in Cleburne?',
        a: 'The full range: home repairs and punch lists, interior and exterior painting, drywall and texture repair, door installation and repair, and trim and finish carpentry.',
      },
      {
        q: 'How do estimates work for Cleburne projects?',
        a: 'Send photos and a description through the estimate form or chat. The owner reviews every request personally and follows up with a written scope in plain language.',
      },
    ],
  },
  {
    slug: 'dfw',
    name: 'Surrounding DFW',
    blurb:
      'We also serve the broader DFW area — Crowley, Joshua, Mansfield, Aledo, Benbrook, and surrounding communities.',
  },
];

// Portfolio photos: real, EXIF-normalized, hosted from /public/portfolio.
// Each item is filterable by category on the /gallery page.
export const GALLERY = [
  {
    id: 'flooring-subfloor-rebuild',
    title: 'Subfloor rebuild & new flooring',
    category: 'Flooring',
    location: 'Weatherford, TX',
    caption:
      'Weatherford, TX — moisture damage hidden under damaged linoleum meant this floor had to come down to the joists: damaged subfloor rebuilt and insulated, then finished with new luxury vinyl plank, transitions, and trim.',
    cover: '/portfolio/flooring-lvp-after.webp',
    media: [
      { type: 'image', src: '/portfolio/flooring-subfloor-before.webp', label: 'Before', alt: 'Before — floor torn out to the joists with moisture-damaged subfloor exposed' },
      { type: 'image', src: '/portfolio/flooring-lvp-after.webp', label: 'After', alt: 'After — new luxury vinyl plank flooring with clean vent and trim detail' },
    ],
    story: [
      'The problem under this Weatherford floor ran deeper than the surface suggested. Damaged linoleum had let moisture through, and the subfloor beneath had been damaged by that moisture. Laying new flooring straight over it would have hidden the problem, not fixed it.',
      'The old floor came up and the damaged area was opened down to the joists. The damaged subfloor was rebuilt and insulated, giving the new flooring a solid, dry base — the part of the job nobody sees once the room is finished, and the part that decides how long the new floor lasts.',
      'With the structure sound, new luxury vinyl plank went down, finished with clean transitions, vent, and trim detail. The before and after photos below show the full arc — from a floor opened to the joists to a finished room.',
    ],
    relatedServices: ['flooring'],
  },
  {
    id: 'drywall-texture-project',
    title: 'Drywall repair & texture match',
    category: 'Drywall',
    caption:
      'Damaged drywall cut back, skimmed, and texture-matched to blend into the surrounding wall — finished with fresh paint so the repair disappears.',
    cover: '/portfolio/drywall-texture-project-poster.jpg',
    media: [
      { type: 'video', src: '/portfolio/drywall-texture-project.mp4', poster: '/portfolio/drywall-texture-project-poster.jpg', alt: 'Drywall repair walkthrough — skim coating, texture matching, and paint' },
    ],
  },
  {
    id: 'deck-staining-mansfield',
    title: 'Deck staining & new-build defect repairs',
    category: 'Outdoor',
    location: 'Mansfield, TX',
    caption:
      'Mansfield, TX — corrected new-build defects on this home\u2019s deck, then finished the full deck surface for a clean, protected result.',
    cover: '/portfolio/deck-staining-mansfield-tx.webp',
    media: [
      { type: 'image', src: '/portfolio/deck-staining-mansfield-tx.webp', alt: 'Freshly stained deck alongside a brick home in Mansfield, TX' },
      { type: 'video', src: '/portfolio/deck-staining-mansfield-clip-1.mp4', poster: '/portfolio/deck-staining-mansfield-clip-1-poster.jpg', alt: 'Deck finish being applied across the boards' },
      { type: 'video', src: '/portfolio/deck-staining-mansfield-clip-2.mp4', poster: '/portfolio/deck-staining-mansfield-clip-2-poster.jpg', alt: 'Close-up of the finish coat on repaired deck boards' },
      { type: 'video', src: '/portfolio/deck-staining-mansfield-clip-3.mp4', poster: '/portfolio/deck-staining-mansfield-clip-3-poster.jpg', alt: 'Deck surface during finishing work' },
      { type: 'video', src: '/portfolio/deck-staining-mansfield-clip-4.mp4', poster: '/portfolio/deck-staining-mansfield-clip-4-poster.jpg', alt: 'Walkthrough of the deck as finishing wraps up' },
    ],
  },
  {
    id: 'patio-refresh',
    title: 'Patio refresh — cedar post wraps',
    category: 'Outdoor',
    caption:
      'Posts re-wrapped in cedar, stained and finished with a UV-resistant top coat. Mortar cracks were treated and filled so they do not re-crack.',
    cover: '/portfolio/patio-refresh-finished.webp',
    media: [
      { type: 'image', src: '/portfolio/patio-refresh-finished.webp', alt: 'Finished porch with dark-stained cedar-wrapped posts' },
      { type: 'video', src: '/portfolio/patio-refresh-walkthrough.mp4', poster: '/portfolio/patio-refresh-walkthrough-poster.jpg', alt: 'Full walkthrough of the patio refresh — cedar post wraps from prep to finish' },
      { type: 'image', src: '/portfolio/patio-refresh-posts.webp', alt: 'Cedar-wrapped porch posts with fresh stain along the brick porch' },
      { type: 'image', src: '/portfolio/patio-refresh-post-detail.webp', alt: 'Detail of a stained cedar post wrap' },
    ],
  },
  {
    id: 'permanent-lighting-roofline',
    title: 'Permanent roofline lighting',
    category: 'Outdoor',
    caption:
      'ShawTech permanent LED roofline lighting — low-profile track that all but disappears by day, and app-controlled color scenes by night.',
    cover: '/portfolio/permanent-lighting-night.webp',
    media: [
      { type: 'image', src: '/portfolio/permanent-lighting-night.webp', alt: 'Roofline glowing with red, white, and blue permanent LED lighting at night' },
      { type: 'image', src: '/portfolio/permanent-lighting-day.webp', alt: 'Low-profile permanent lighting track mounted along the roofline, barely visible by day' },
    ],
  },
  {
    id: 'wood-double-doors',
    title: 'Stained wood double-door install',
    category: 'Doors',
    caption: 'Custom mahogany-stained double doors with divided-light glass and an arched brick transom, set on a painted-white brick home.',
    src: '/portfolio/wood-double-doors.webp',
    aspect: 4 / 5,
  },
  {
    id: 'white-double-doors',
    title: 'White glass double-door install',
    category: 'Doors',
    caption: 'Full-lite white double doors with keypad lock — clean install with tight jamb reveal and a straight threshold on a painted brick porch.',
    src: '/portfolio/white-double-doors.webp',
    aspect: 3 / 4,
  },
  {
    id: 'door-prep-paint',
    title: 'Exterior door prep & repaint',
    category: 'Painting',
    caption: 'Doors pulled, masked, and sprayed on-site — proper prep before rehang for a clean, factory-grade finish.',
    src: '/portfolio/door-prep-paint.webp',
    aspect: 3 / 4,
  },
  {
    id: 'commercial-diamond-wall',
    title: 'Commercial exterior repaint',
    category: 'Painting',
    caption: 'Large-format exterior repaint on a commercial pickup-lane building — crisp diamond-pattern trim over a solid white field.',
    src: '/portfolio/commercial-diamond-wall.webp',
    aspect: 4 / 3,
  },
  {
    id: 'black-door-red-trim',
    title: 'Exterior door install with painted trim',
    category: 'Doors',
    caption: 'Black fiberglass exterior door with keypad lock, set in a hand-painted red-orange brick-mold trim on brick.',
    src: '/portfolio/black-door-red-trim.webp',
    aspect: 3 / 4,
  },
  {
    id: 'interior-trim',
    title: 'Interior trim & baseboard finish',
    category: 'Painting',
    caption: 'Baseboard and casing painted with clean lines against a knockdown-textured wall.',
    src: '/portfolio/interior-trim.webp',
    aspect: 4 / 3,
  },
  {
    id: 'dresser-refinish',
    title: 'Refinished dresser',
    category: 'Painting',
    caption: 'Sanded, primed, and repainted in deep satin black — front-porch reveal.',
    src: '/portfolio/dresser-refinish.webp',
    aspect: 3 / 4,
  },
  {
    id: 'lattice-patio',
    title: 'Cedar lattice privacy screen',
    category: 'Carpentry',
    caption: 'Custom cedar lattice screen with integrated planter boxes on a concrete patio.',
    src: '/portfolio/lattice-patio.webp',
    aspect: 4 / 3,
  },
  {
    id: 'fence-gate-close',
    title: 'Custom cedar gate with cable bracing',
    category: 'Carpentry',
    caption: 'Cedar privacy gate with steel hardware, cable diagonals, and self-closing hinges.',
    src: '/portfolio/fence-gate-close.webp',
    aspect: 3 / 4,
  },
  {
    id: 'fence-gate-collage',
    title: 'Fence & gate project — from plan to install',
    category: 'Carpentry',
    caption: 'From CAD plan to finished install: full fence and gate build-out.',
    src: '/portfolio/fence-gate-collage.webp',
    aspect: 1,
  },
  {
    id: 'home-gym-build',
    title: 'Home gym build — full room transformation',
    category: 'Carpentry',
    caption:
      'A full room build: dark accent walls, mounted mirrors, crown moulding, a structural heavy-bag mount, and a custom hex LED ceiling grid over knockdown texture.',
    cover: '/portfolio/gym-wide.webp',
    media: [
      { type: 'image', src: '/portfolio/gym-wide.webp', alt: 'Complete home-gym room — dark accent walls, crown moulding, and a hex LED ceiling grid' },
      { type: 'image', src: '/portfolio/gym-hex-doorway.webp', alt: 'View from the doorway — dark accent walls, mounted mirrors, hex LED ceiling' },
      { type: 'image', src: '/portfolio/gym-bag-hex.webp', alt: 'Ceiling-mounted heavy bag anchor with clean crown moulding and hex LED lighting' },
      { type: 'image', src: '/portfolio/gym-hex-ceiling.webp', alt: 'Close-up of the custom hex LED ceiling grid over a knockdown-textured ceiling' },
    ],
  },
];

export const GALLERY_CATEGORIES = ['All', 'Doors', 'Painting', 'Carpentry', 'Outdoor', 'Drywall', 'Flooring'];

export const WHY_CHOOSE = [
  {
    icon: 'UserCheck',
    title: 'Owner-led work',
    text: 'The owner is on the job — not a rotating crew of subs you never meet.',
  },
  {
    icon: 'MessageCircle',
    title: 'Clear communication',
    text: 'You know what is happening, what it costs, and when it will be done.',
  },
  {
    icon: 'Home',
    title: 'Respect for the home',
    text: 'Clean job sites, dropcloths where they belong, and doors that get closed at the end of the day.',
  },
  {
    icon: 'Shield',
    title: 'Durable materials & proper prep',
    text: 'Prep is where jobs last or fail. We do it right so the finish holds up.',
  },
  {
    icon: 'FileText',
    title: 'Honest estimates',
    text: 'Scope in plain language. No surprise change orders invented mid-job.',
  },
  {
    icon: 'BadgeCheck',
    title: 'BBB accredited',
    text: 'A business built around trust, communication, and doing the work correctly.',
  },
];

export const PROCESS_STEPS = [
  { n: '01', title: 'Send project details & photos — or schedule an in-person estimate', text: 'Share what needs to be done with photos and measurements, or book an on-site visit if the scope is easier to walk through in person.' },
  { n: '02', title: 'Review scope & options', text: 'We talk through the work, materials, and any options that matter.' },
  { n: '03', title: 'Receive a clear estimate', text: 'A written estimate with scope in plain language.' },
  { n: '04', title: 'Schedule the work', text: "We set a start date that fits your schedule and ours." },
  { n: '05', title: 'Complete the project', text: 'Steady progress and communication throughout — until it is done right.' },
];

export const BLOG_POSTS = [
  {
    slug: 'prep-work-matters',
    title: "The Work You Don't See Is the Work That Matters",
    iso: '2026-07-20',
    date: 'Jul 2026',
    read: '3 min',
    excerpt:
      "Most people judge a paint job by the color on the wall. But the part that determines how a job holds up isn't what anyone notices on day 1 — it's the prep.",
    body: `Most people judge a paint job by the color on the wall. Or a repair by how clean the finished patch looks. That's fair — it's what you can see. But the part that actually determines how a job holds up isn't the part anyone notices on day 1.

It's the prep.

## What "prep" actually means

Before we ever open a can of paint or cut a piece of trim, there's a list of unglamorous steps:

- Cleaning and degreasing surfaces so materials actually bond
- Filling nail holes, cracks, and old damage — not just skimming over them
- Sanding down high spots and rough patches
- Taping and masking so lines come out sharp
- Checking for moisture, rot, or movement before covering it up

None of this shows up in a "before and after" photo. But skip it, and the finished work fails early — paint peels, caulk cracks, patches show through in the wrong light.

## Why we don't shortcut it

It would be easy to skip a step here and there. Nobody would know until later — sometimes months later, after we're gone. That's exactly why we don't do it.

Craftsmanship isn't really about the finish coat. It's about what's underneath holding up. If we wrap a rotten board instead of replacing it, or paint over a crack instead of filling it right, we've just handed you a problem with a nice coat of paint on it.

## What this means for your estimate

If you've ever wondered why one quote is lower than another, prep work is usually where the difference is. A shortcut estimate skips it. An honest one accounts for it — the time, the materials, the extra hour of sanding nobody sees.

We'd rather explain that up front than have you find out the hard way six months in.

Planning painting, drywall, or trim work? Our [interior painting](/services/interior-painting) and [exterior painting](/services/exterior-painting) estimates spell out prep in writing — and [what a good painting estimate should include](/blog/good-painting-estimate) is worth a read before you compare any quotes. [Request an estimate](/estimate) and judge ours by that standard.`,
  },
  {
    slug: 'trim-carpentry-cost-guide',
    title: 'How Much Does Trim & Carpentry Cost in Fort Worth and Burleson?',
    iso: '2026-07-01',
    excerpt:
      'Baseboards, door casing, crown molding, and closet shelving – what drives the price of trim carpentry in the Fort Worth and Burleson area, and what to check before you hire.',
    date: 'Jul 2026',
    read: '6 min',
    body: `Trim is the part of a house nobody notices when it's done right – and the first thing anyone notices when it's done wrong. Crooked baseboards, gapped casing, and mismatched crown molding stand out in a room even to people who couldn't tell you why something looks off. In the Fort Worth and Burleson area, most trim jobs run $4–$12 per linear foot installed, depending on the type of trim, the material, and how much of the house it's touching. Here's how that breaks down, and what actually moves the price.

## What "trim carpentry" covers

It's a broad category, and most homes need more than one kind at some point:

- Baseboards – the trim running along the floor where it meets the wall
- Quarter round / shoe molding – the small strip that covers the gap between baseboard and flooring, especially common after a flooring replacement
- Door and window casing – the trim framing doors and windows
- Crown molding – trim where the wall meets the ceiling
- Closet and shelving – built-in shelving, closet systems, and small storage carpentry
- Small framing – minor structural carpentry that supports the above (blocking, furring, small wall modifications)

Most jobs combine two or three of these – replacing baseboard after new flooring almost always means new quarter round too, for example.

## What drives the cost

**Material.** Paint-grade MDF or finger-jointed pine is the standard for painted trim and the most affordable option. Solid poplar or stain-grade hardwood costs more but is worth it if the trim will be stained rather than painted, since MDF doesn't take stain well.

**Profile and size.** A simple 3½" baseboard costs less to install than a tall, multi-piece baseboard or a built-up crown molding with several profile pieces stacked together.

**Condition of the walls and floors.** This is the one homeowners underestimate most. Texas homes shift with the seasonal clay soil, and by the time a house is a few years old, walls are rarely perfectly straight or floors perfectly level. Scribing trim to fit an uneven wall – cutting it to follow the actual contour instead of leaving gaps – takes real time. A quote that doesn't account for this is a quote that's going to need change orders later.

**Paint or finish.** Trim almost always gets caulked, filled, sanded, and painted or stained after installation. That labor is often a bigger share of the job than the material itself, and it's easy for a lower bid to be quietly skipping it. We wrote about that pattern in [Why the Cheapest Contractor Can Cost More in the Long Run](/blog/cheapest-contractor-costs-more) – trim is one of the most common places it shows up.

**Access and removal.** Furniture that needs moving, existing trim that has to be pried off without damaging drywall, and baseboards behind cabinetry or built-ins all add time.

## Rough price ranges

These are typical installed ranges for the DFW area, painted and finished, not just material cost:

| Trim type | Typical range |
|---|---|
| Baseboard (per linear ft.) | $4–$8 |
| Quarter round / shoe molding (per linear ft.) | $2–$4 |
| Door casing (per opening) | $150–$300 |
| Window casing (per opening) | $150–$350 |
| Crown molding (per linear ft.) | $6–$12 |
| Closet shelving (per closet, basic) | $200–$500 |

A single room getting new baseboard and quarter round after a flooring job is often a half-day to full-day project. Whole-house trim replacement is a multi-day job and priced accordingly.

## Why quarter round is its own line item

Quarter round comes up constantly, usually as an add-on to a flooring replacement – the flooring crew pulls the old baseboard trim, installs new floors, and doesn't put shoe molding back, or the new floor's thickness means the old quarter round no longer fits the gap. It's a small material cost, but it still needs to be primed, nailed, caulked, and painted to match – and it's the kind of finishing detail that makes a floor replacement look actually finished instead of half-done.

## DIY vs. hiring it out

Painting-grade baseboard in a single square room with straight walls is a reasonable DIY project for someone comfortable with a miter saw. Where it gets harder fast:

- Inside and outside corners that aren't square (most aren't)
- Crown molding, which involves compound angles
- Matching an existing profile that's no longer sold – older Fort Worth and Burleson homes often have trim profiles that were discontinued years ago
- Any run longer than one room, where small cutting errors compound

If a wall is visibly bowed or a floor visibly out of level, that's usually the point where scribing skill matters more than the tool.

## What to ask before you hire

A trim estimate worth trusting should tell you, in plain language:

1. What material is being used (MDF, pine, poplar) and why
2. Whether existing trim removal and disposal is included
3. Whether caulk, fill, and paint are included or a separate line item
4. How uneven walls or floors will be handled – scribed to fit, or shimmed
5. Whether the quote is per linear foot, per room, or a flat project price

If those five things aren't answered before you sign, they'll get answered mid-project – usually at your expense.

## HearthOak Construction

We handle trim and carpentry work across Fort Worth, Burleson, Weatherford, Granbury, Cleburne, and the surrounding DFW area – from a single room of quarter round to full-house baseboard, casing, and crown molding. See our [Trim & Carpentry services](/services/trim-carpentry) or [request an estimate](/estimate) with photos and we'll get back to you within 24 hours.`,
  },
  {
    slug: 'cheapest-contractor-costs-more',
    title: 'Why the Cheapest Contractor Can Cost More in the Long Run',
    iso: '2025-11-20',
    excerpt:
      'A lower bid usually means something got skipped. Here is what usually gets cut — and what it actually costs the homeowner.',
    date: 'Nov 2025',
    read: '5 min',
    body: `Everyone likes a low number on a bid. But construction and repair work is a place where the cheapest price almost always hides something.

There are only a few ways to make a job cheaper. You use cheaper materials, you skip prep work, you rush the labor, or you skip warranty and follow-up. On a small job that might be fine. On paint, doors, drywall, or trim work in a home you plan to keep, it usually is not.

Here is what typically gets cut when a contractor bids low:

Prep work. Prep is the single biggest driver of how long a paint job lasts, how flush a drywall patch looks, and whether a door swings properly a year from now. It is also invisible on the invoice, which makes it the first thing to disappear.

Material grade. There is a real difference between a bargain exterior paint and a coating built for Texas sun. There is a real difference between a hollow-core interior door and a solid-core one. Cheap bids almost always spec the cheaper option, sometimes without telling the homeowner.

Finish work. Caulk lines, reveals, mitered corners, and trim details are what the eye actually notices. Cheap jobs finish fast because they skip these last steps.

Communication and follow-up. A cheap crew is usually a busy crew that has to move to the next job to stay profitable. Small punch-list items get left behind.

None of that shows up on the initial bid. It shows up six months later, when the paint is peeling, the door is sticking, or the drywall patch is telegraphing through the wall.

The alternative is not paying more for the sake of it. It is paying for the parts of the job that make it last: proper prep, better materials, clean finish work, and a contractor who actually returns your call.

If you are comparing bids, ask the low bidder specifically what prep is included, what grade of materials they are using, and what happens if something needs to be corrected after the fact. Their answers will tell you what you are actually buying.

Comparing bids for painting, drywall, door, or trim work in Fort Worth or the surrounding DFW area? We are glad to walk you through our scope line by line. See [our services](/services) or [request an estimate](/estimate) and judge our estimate by the same standard.`,
  },
  {
    slug: 'replacing-exterior-door',
    title: 'What to Know Before Replacing an Exterior Door',
    iso: '2025-11-10',
    excerpt: 'Fit, weather sealing, brick mold, and threshold — the details that separate a real install from a cosmetic swap.',
    date: 'Nov 2025',
    read: '6 min',
    body: `Replacing an exterior door is one of those projects that looks simple until you actually pull the old one out. Here is what actually matters.

Rough opening and squareness. Older Texas homes settle. The rough opening is rarely perfectly square, and a new pre-hung door will not swing correctly if it is not shimmed and set right. This is where most bad installs go wrong.

Threshold and sill. The threshold is what stops water. If it is not sealed to the floor and to the door jamb properly, water finds its way in. On exterior doors this leads to rot on the sill plate and to soft spots on interior flooring.

Brick mold and exterior trim. The exterior trim around the door — brick mold on brick homes, casing on siding homes — carries most of the weather load. Old brick mold usually needs to be replaced when a door is replaced. Caulking it back over the old rotten piece is a shortcut you will see fail within a year.

Weather stripping and sweep. New doors come with weather stripping but they need to be adjusted so they compress evenly around the frame. The sweep at the bottom needs to actually touch the threshold. This is finish work that gets skipped on rushed jobs.

Locks, strike plates, and hinges. A door that closes but does not latch cleanly is a security and comfort issue. Strike plates need to be shimmed and aligned. Hinges need to be set flush. Cheap installs skip this step.

Interior side finish. Once the door is in, there is casing, caulking, and paint or stain to complete. A good install leaves the interior side ready to paint. A bad one leaves visible gaps between the jamb and the drywall.

When you are getting an estimate for a door replacement, ask what happens with the brick mold, what happens with the threshold seal, and what the finished interior side will look like. That conversation tells you whether you are getting a real install or a cosmetic swap.

We install and repair exterior and interior doors across Fort Worth, Burleson, Weatherford, Granbury, and Cleburne. See our [door installation & repair services](/services/door-installation-repair) or [request an estimate](/estimate) with photos of the opening.`,
  },
  {
    slug: 'signs-home-needs-exterior-paint',
    title: 'How to Tell If Your Home Needs Exterior Paint',
    iso: '2025-10-21',
    excerpt: 'Chalking, hairline cracking, fading, and caulk failure — the signs that mean it is time before real damage sets in.',
    date: 'Oct 2025',
    read: '5 min',
    body: `Exterior paint protects a home from water, sun, and temperature swings. When it fails, it usually gives you warning signs before real damage sets in. Here is what to look for.

Chalking. Rub your hand on a painted surface. If you get a chalky residue, the paint has broken down and is no longer sealing properly. This is common on older exterior latex jobs.

Hairline cracking. Look at caulk lines, siding seams, and trim joints. Hairline cracks are entry points for water. Left long enough, that water gets behind the paint and into the substrate.

Fading and color loss. Fading is cosmetic on its own, but it also signals that the top coat has lost its UV protection. Once that goes, breakdown accelerates.

Peeling or blistering. Peeling is a late-stage failure. Blistering usually means water got behind the paint film and is trying to escape. Both mean paint has already stopped protecting the surface.

Caulk failure. Caulk shrinks and cracks with age. Around windows, doors, trim, and siding transitions, failed caulk is where most water intrusion actually happens.

Bare or exposed wood. Once the substrate is exposed, damage is happening every week the sun and rain hit it. This is the point where a repaint stops being cosmetic and starts being repair-with-a-repaint.

If you see two or three of these on your home, it is likely time. A good exterior repaint at this stage is much less expensive than one done after the substrate has been damaged.

We handle prep-first [exterior painting](/services/exterior-painting) across Fort Worth, Burleson, Weatherford, Granbury, and Cleburne. [Request an estimate](/estimate) and we will walk the property and tell you honestly what it needs.`,
  },
  {
    slug: 'drywall-texture-matching-skill',
    title: 'Why Drywall Texture Matching Takes Skill',
    iso: '2025-10-12',
    excerpt: 'Knockdown, orange peel, hand texture — matching them under paint is where drywall repair actually gets judged.',
    date: 'Oct 2025',
    read: '4 min',
    body: `The measure of a good drywall repair is whether you can see it after the wall is painted. That sounds obvious. In practice, matching existing wall texture is where most repairs quietly fail.

Texture is not one thing. Knockdown, orange peel, popcorn ceiling, hand-troweled skim, and various regional textures all behave differently. Each has a specific mud consistency, nozzle setup, and knockdown timing.

Blending is the real work. Even when the texture type is matched, the blend at the edge of the patch is what your eye picks up. A blend that stops at the edge of the patch will telegraph through the paint. A good drywall finisher feathers the texture out into the surrounding wall so there is no visible boundary.

Light matters. Textures look different in raking light — the kind of light that skims across a wall at a low angle from a window or a lamp. That is why bad patches show up worst in the evening. Testing under raking light before priming is how good finishers catch their own work.

Primer and paint matter too. New drywall mud absorbs paint differently than the surrounding wall. Without a good primer, the patch can flash — meaning it looks slightly different in sheen even after two coats. Priming the repair before top coats is not optional.

If you have had a leak, a wall repair, or a doorway patched, the finish work is what you actually judge afterward. Ask about texture matching, primer, and blending explicitly.

Need a patch that disappears? See our [drywall, texture & repair services](/services/drywall-texture-repairs) or [request an estimate](/estimate) with a photo of the damage.`,
  },
  {
    slug: 'good-painting-estimate',
    title: 'What Makes a Good Painting Estimate',
    iso: '2025-10-02',
    excerpt: 'Scope, prep, coats, product, and exclusions — what a real estimate should say before you sign.',
    date: 'Oct 2025',
    read: '5 min',
    body: `A painting estimate is one of the most-shopped items in home services, and one of the most misleading. Two bids that look similar in price can be very different in what they actually include.

Here is what a good painting estimate should tell you.

Scope. Which surfaces are being painted, in which rooms, and in which colors. Trim, ceilings, doors, and closets should each be called out. If the estimate says only "paint the interior," you do not have a real scope.

Prep work. Filling nail holes, caulking, sanding, spot priming, and masking. This should be described specifically. If prep is not in the estimate, expect it to be minimal.

Number of coats. Two coats is standard on most jobs. Some colors and some substrates require three. A one-coat estimate should raise an immediate question.

Product. Manufacturer, product line, and sheen. There is a real difference between builder-grade paint and a good-quality interior or exterior line. On exteriors, a product like Sherwin-Williams Duration behaves differently from a bargain equivalent.

Exclusions. What is not included. Furniture moving, wall repair, wallpaper removal, and cabinet painting are common exclusions. Better to see them called out than assumed.

Timeline and payment. Roughly when the job will start, roughly how long it will take, and how payment is structured. Reasonable painters do not ask for the full price up front.

If the estimate you receive covers these points in writing, you are likely dealing with someone who intends to do the job the same way they wrote it up.

Our [interior](/services/interior-painting) and [exterior painting](/services/exterior-painting) estimates are written to exactly this standard. [Request an estimate](/estimate) and compare it against this list.`,
  },
  {
    slug: 'prepare-small-home-repair',
    title: 'How to Prepare for a Small Home Repair Project',
    iso: '2025-09-15',
    excerpt: 'A few simple steps that make repair projects go faster, cost less, and finish cleaner.',
    date: 'Sep 2025',
    read: '4 min',
    body: `Small repair projects — a door that will not latch, a drywall patch, a piece of trim that came loose, a section of paint that needs redone — go smoother when a few things are ready before the contractor shows up.

Clear the area. Move furniture, wall art, and rugs away from the work area if you can. This is the single most helpful thing a homeowner can do. It saves labor time and it keeps your belongings out of dust and paint.

Take photos before. Send photos of the issue when you request the estimate. If the issue is intermittent — a door that only sticks in humidity, a stain that shows in certain light — photos help us understand it faster.

Note related issues. If the drywall crack is one of several in the house, mention that. If the door is one of two similar doors that both need work, mention that. Grouping small jobs saves labor and mobilization costs.

Decide on colors and finishes early. If paint is involved, having the color chosen before the crew arrives avoids a delay day. Same for door hardware finishes, if hardware is being changed.

Plan for pets and kids. Repair work involves tools, ladders, and open cans. Knowing where pets and kids will be during work hours helps everyone.

Be reachable. Small questions come up on repair jobs — a decision about caulk color, a question about a piece of trim. A quick answer keeps the work moving.

None of this is complicated. It is the difference between a repair job that finishes in one visit and one that takes three.

Have a repair list building up? See our [small construction & repair services](/services/small-construction-repair) or [send photos through the estimate form](/estimate) and we will scope them together.`,
  },
];
